import { queryOptions } from "@tanstack/react-query";
import { listHeroSlides } from "./slides.functions";

export type GlassAlignment =
  | "top-left"
  | "top-center"
  | "top-right"
  | "left"
  | "center"
  | "right"
  | "bottom-left"
  | "bottom-center"
  | "bottom-right";

export const GLASS_ALIGNMENTS: GlassAlignment[] = [
  "top-left", "top-center", "top-right",
  "left", "center", "right",
  "bottom-left", "bottom-center", "bottom-right",
];

/**
 * Same nine tokens as GlassAlignment: which part of the image the hero crop
 * anchors to. The slideshow also uses it as the transform-origin for motion
 * effects, so a zoom holds this point on screen for the whole animation.
 */
export type ImagePosition = GlassAlignment;

/** Frosted panel width on md+ screens. NULL renders the legacy 80% width. */
export type GlassSize = "sm" | "md" | "lg";

export const GLASS_SIZES: { value: GlassSize; label: string }[] = [
  { value: "sm", label: "Small" },
  { value: "md", label: "Medium" },
  { value: "lg", label: "Large" },
];

export type SlideEffect =
  | "kenburns"
  | "kenburns-reverse"
  | "zoom-in"
  | "zoom-out"
  | "pan-left"
  | "pan-right"
  | "none";

export type HeroSlideRow = {
  id: string;
  kicker: string;
  title: string;
  subtitle: string;
  image_url: string | null;
  image_path: string | null;
  cta_primary_label: string | null;
  cta_primary_href: string | null;
  cta_secondary_label: string | null;
  cta_secondary_href: string | null;
  effect: SlideEffect;
  glass_alignment: GlassAlignment | null;
  image_position: ImagePosition | null;
  glass_size: GlassSize | null;
  glass_bg_color: string | null;
  glass_bg_opacity: number | null;
  kicker_color: string | null;
  title_color: string | null;
  subtitle_color: string | null;
  cta_primary_bg: string | null;
  cta_primary_fg: string | null;
  cta_secondary_bg: string | null;
  cta_secondary_fg: string | null;
  is_active: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
};

export const heroSlidesQueryOptions = () =>
  queryOptions({
    queryKey: ["hero-slides"],
    queryFn: async () => {
      const data = await listHeroSlides();
      return data as HeroSlideRow[];
    },
  });

export const SLIDE_EFFECTS: { value: SlideEffect; label: string }[] = [
  { value: "kenburns", label: "Ken Burns (zoom + drift)" },
  { value: "kenburns-reverse", label: "Ken Burns reverse" },
  { value: "zoom-in", label: "Slow zoom in" },
  { value: "zoom-out", label: "Slow zoom out" },
  { value: "pan-left", label: "Pan left" },
  { value: "pan-right", label: "Pan right" },
  { value: "none", label: "None (static)" },
];

export const SLIDE_COLOR_DEFAULTS = {
  kicker_color: "#0ea5e9",
  title_color: "#0f172a",
  subtitle_color: "#334155",
  cta_primary_bg: "#0f172a",
  cta_primary_fg: "#ffffff",
  cta_secondary_bg: "#ffffff",
  cta_secondary_fg: "#0f172a",
  // Frosted panel background. NULL in the row = the legacy look, which is what
  // these two values reproduce (bg-white/20 under backdrop-filter).
  glass_bg_color: "#ffffff",
  glass_bg_opacity: 20,
} as const;

/**
 * "#rgb" or "#rrggbb" + a 0-100 percent opacity -> "rgba(r, g, b, a)".
 * Unparsable input falls back to white so the panel never loses its backdrop.
 */
export function hexToRgba(hex: string, opacityPercent: number): string {
  let h = (hex || "").trim().replace(/^#/, "");
  if (/^[0-9a-f]{3}$/i.test(h)) h = h.split("").map((c) => c + c).join("");
  if (!/^[0-9a-f]{6}$/i.test(h)) h = "ffffff";
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const pct = Number.isFinite(opacityPercent) ? opacityPercent : 0;
  const a = Math.min(100, Math.max(0, pct)) / 100;
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}
